import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Layers,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Server,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { pushToast } from "@/components/Toaster";
import {
  PROXY_PHYSICAL_TYPES,
  type ProxyBulkResult,
  type ProxyProbeResult,
  type ProxySummary,
  type ProxyType,
  parseProxyLine,
  useBulkCreateProxies,
  useCreateProxy,
  useDeleteProxy,
  useProbeProxy,
  useProxies,
  useTestAllProxies,
  useTestProxy,
  useUpdateProxy,
} from "@/lib/proxies";
import { Button } from "@/components/ui/button";
import { CounterButton } from "@/components/ui/counter-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/ui/badge";
import { ActionMenu } from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Alert } from "@/components/ui/alert";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { SimpleSelect } from "@/components/ui/select";
import { HelpSection } from "@/components/HelpSection";
import { formatRelative } from "@/lib/format";

// "mtproxy" is deliberately absent: the backend can't build a working Telethon
// connection for it (wrong tuple shape, needs ConnectionTcpMTProxy*), so every
// account assigned to one fails to connect. Offering it in the picker only
// produced proxies that looked configured and silently never worked. The type
// still exists in ProxyType so any legacy row keeps rendering.
const TYPES: ProxyType[] = ["socks5", "http", "https"];

// Above this MTProto round-trip (ms) a proxy is "alive but slow" — high ping
// stalls automation (GramGPT flags the same). Advisory only; doesn't gate.
const SLOW_PROXY_MS = 1500;

type HealthFilter = "all" | "alive" | "dead" | "unknown";

function proxyHealth(p: ProxySummary): Exclude<HealthFilter, "all"> {
  return p.is_alive === null ? "unknown" : p.is_alive ? "alive" : "dead";
}

function matchesProxyQuery(p: ProxySummary, raw: string): boolean {
  const q = raw.trim().toLowerCase();
  if (!q) return true;
  return [p.label, p.host, `${p.host}:${p.port}`, p.country_code, p.country_name, p.city].some(
    (v) => v?.toLowerCase().includes(q),
  );
}

/**
 * Navigate using the same mechanism as App.tsx's hand-rolled router
 * (pushState + a synthetic popstate) — the accounts count links into a
 * pre-filtered Accounts page.
 */
function navigate(to: string) {
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/* ----------------------------------------------------------- ProxiesPage */

export function ProxiesPage() {
  const { t } = useTranslation();
  const proxies = useProxies();
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProxySummary | null>(null);
  const [editTarget, setEditTarget] = useState<ProxySummary | null>(null);
  const [healthFilter, setHealthFilter] = useState<HealthFilter>("all");
  const [query, setQuery] = useState("");
  const test = useTestProxy();
  const testAll = useTestAllProxies();

  const runTestAll = () => {
    testAll.mutate(undefined, {
      onSuccess: (rows) => {
        const testable = rows.filter((p) => p.type !== "mtproxy");
        const alive = testable.filter((p) => p.is_alive === true).length;
        const dead = testable.filter((p) => p.is_alive === false).length;
        pushToast({
          kind: dead > 0 ? "warning" : "success",
          text: t("proxies.test_all_done", {
            total: testable.length,
            alive,
            dead,
          }),
        });
      },
      onError: (err) => {
        pushToast({
          kind: "error",
          text: t("proxies.test_all_error"),
          detail: err instanceof ApiError ? err.detail : String(err),
        });
      },
    });
  };

  const data = useMemo(() => proxies.data ?? [], [proxies.data]);

  // Counters are taken over the search, so each is what clicking it lists.
  const searched = useMemo(() => data.filter((p) => matchesProxyQuery(p, query)), [data, query]);
  const healthCounts = useMemo(() => {
    const c = { alive: 0, dead: 0, unknown: 0 };
    for (const p of searched) c[proxyHealth(p)] += 1;
    return c;
  }, [searched]);
  const filtered = useMemo(
    () =>
      healthFilter === "all" ? searched : searched.filter((p) => proxyHealth(p) === healthFilter),
    [searched, healthFilter],
  );

  const subtitle =
    data.length === 0 ? (
      t("proxies.subtitle")
    ) : (
      <span className="-ml-2 flex flex-wrap items-center gap-0.5">
        <CounterButton
          label={t("proxies.counters.total")}
          value={searched.length}
          selected={healthFilter === "all"}
          onClick={() => setHealthFilter("all")}
        />
        <CounterButton
          label={t("proxies.counters.alive")}
          dot="bg-success"
          value={healthCounts.alive}
          selected={healthFilter === "alive"}
          onClick={() => setHealthFilter("alive")}
        />
        <CounterButton
          label={t("proxies.counters.dead")}
          dot="bg-danger"
          value={healthCounts.dead}
          tone="danger"
          selected={healthFilter === "dead"}
          onClick={() => setHealthFilter("dead")}
        />
        {(healthCounts.unknown > 0 || healthFilter === "unknown") && (
          <CounterButton
            label={t("proxies.counters.unknown")}
            value={healthCounts.unknown}
            selected={healthFilter === "unknown"}
            onClick={() => setHealthFilter("unknown")}
          />
        )}
      </span>
    );

  const columns = useMemo<ColumnDef<ProxySummary, unknown>[]>(
    () => [
      {
        id: "label",
        header: t("proxies.col.label"),
        accessorFn: (p) => p.label,
        cell: ({ row }) => {
          const p = row.original;
          return (
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-semibold text-fg-primary">{p.label}</span>
                {p.has_auth && (
                  <Tooltip content={t("proxies.has_auth_hint")}>
                    <Lock
                      size={13}
                      strokeWidth={2}
                      aria-hidden="true"
                      className="shrink-0 text-fg-muted"
                    />
                  </Tooltip>
                )}
              </div>
              {p.notes && (
                <p className="truncate text-xs text-fg-muted italic">{p.notes}</p>
              )}
            </div>
          );
        },
      },
      {
        id: "endpoint",
        header: t("proxies.col.endpoint"),
        accessorFn: (p) => `${p.type}://${p.host}:${p.port}`,
        cell: ({ row }) => {
          const p = row.original;
          // The scheme is the same for a whole fleet and ate a third of the
          // column, pushing later columns off-screen. Keep host:port visible
          // and park the full endpoint in the tooltip.
          return (
            <Tooltip content={`${p.type}://${p.host}:${p.port}`}>
              <span className="font-mono text-xs text-fg-secondary">
                {p.host}:{p.port}
              </span>
            </Tooltip>
          );
        },
      },
      {
        id: "country",
        header: t("proxies.col.country"),
        accessorFn: (p) => p.country_code ?? "",
        cell: ({ row }) => {
          const p = row.original;
          if (!p.country_code) return <span className="text-fg-muted">—</span>;
          return (
            <span className="text-fg-secondary">
              {p.country_code}
              {p.city && (
                <span className="ml-1 text-fg-muted">({p.city})</span>
              )}
            </span>
          );
        },
      },
      {
        id: "health",
        header: t("proxies.col.health"),
        accessorFn: (p) =>
          p.is_alive === null ? "unknown" : p.is_alive ? "alive" : "dead",
        cell: ({ row }) => {
          const p = row.original;
          const statusVal =
            p.is_alive === null ? "unknown" : p.is_alive ? "alive" : "dead";
          const label =
            p.is_alive === null
              ? t("proxies.health.unknown")
              : p.is_alive
                ? t("proxies.health.alive")
                : t("proxies.health.dead");
          const badge = (
            <div className="flex w-fit items-center gap-2">
              <StatusBadge domain="proxy" value={statusVal} label={label} dot />
              {p.latency_ms != null &&
                p.is_alive &&
                (p.latency_ms >= SLOW_PROXY_MS ? (
                  <Tooltip content={t("proxies.slow_ping_hint")}>
                    <span className="font-mono text-xs text-warning">{p.latency_ms} ms ⚠</span>
                  </Tooltip>
                ) : (
                  <span className="font-mono text-xs text-fg-muted">{p.latency_ms} ms</span>
                ))}
            </div>
          );
          // How fresh the verdict is — a days-old "alive" is a guess.
          const checked = (
            <span className="block text-xs text-fg-muted">
              {p.last_check_at
                ? t("proxies.checked", { when: formatRelative(p.last_check_at) })
                : t("proxies.never_checked")}
            </span>
          );
          return (
            <div className="space-y-1">
              {p.is_alive === false && p.last_check_error ? (
                <Tooltip content={p.last_check_error}>{badge}</Tooltip>
              ) : (
                badge
              )}
              {checked}
            </div>
          );
        },
      },
      {
        id: "accounts",
        header: t("proxies.col.accounts"),
        accessorFn: (p) => p.assigned_accounts_count,
        cell: ({ row }) => {
          const p = row.original;
          if (p.assigned_accounts_count === 0) {
            return <span className="font-mono text-sm text-fg-muted">0</span>;
          }
          return (
            <Tooltip content={t("proxies.accounts_link")}>
              <button
                type="button"
                onClick={() => navigate(`/accounts?proxy=${encodeURIComponent(p.id)}`)}
                className="rounded font-mono text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {p.assigned_accounts_count}
              </button>
            </Tooltip>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("proxies.col.actions")}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original;
          const isTesting =
            (test.isPending && test.variables === p.id) || testAll.isPending;
          const canTest = p.type !== "mtproxy";
          const runTest = () => {
            if (!canTest) return;
            test.mutate(p.id, {
              onSuccess: (updated) => {
                const alive = updated.is_alive;
                pushToast({
                  kind: alive ? "success" : "warning",
                  text: alive
                    ? t("proxies.test_ok", {
                        label: p.label,
                        ms: updated.latency_ms ?? "—",
                      })
                    : t("proxies.test_dead", { label: p.label }),
                });
              },
              onError: (err) => {
                pushToast({
                  kind: "error",
                  text: t("proxies.test_error"),
                  detail: err instanceof ApiError ? err.detail : String(err),
                });
              },
            });
          };
          return (
            <span
              className="flex items-center justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Dedicated test button — high-frequency action, kept out of "…" */}
              <Tooltip content={canTest ? undefined : t("proxies.mtproxy_no_test")}>
                <Button
                  size="sm"
                  variant="outline"
                  iconLeft={<RefreshCw size={14} strokeWidth={2} aria-hidden="true" />}
                  loading={isTesting}
                  disabled={!canTest || test.isPending || testAll.isPending}
                  onClick={runTest}
                >
                  {isTesting ? t("proxies.testing") : t("proxies.test")}
                </Button>
              </Tooltip>
              <ActionMenu
                align="end"
                items={[
                  {
                    label: t("proxies.edit"),
                    icon: <Pencil size={14} strokeWidth={2} aria-hidden="true" />,
                    onSelect: () => setEditTarget(p),
                  },
                  {
                    label: t("proxies.delete"),
                    icon: <Trash2 size={14} strokeWidth={2} aria-hidden="true" />,
                    destructive: true,
                    separatorBefore: true,
                    onSelect: () => setDeleteTarget(p),
                  },
                ]}
              />
            </span>
          );
        },
      },
    ],
    [t, test, testAll.isPending],
  );

  return (
    <TooltipProvider delayDuration={300}>
      <section className="mx-auto max-w-7xl px-6 py-8 space-y-6">
        <PageHeader
          title={t("proxies.title")}
          subtitle={subtitle}
          actions={
            <>
              <Button
                variant="outline"
                iconLeft={<RefreshCw size={16} strokeWidth={2} aria-hidden="true" />}
                loading={testAll.isPending}
                disabled={
                  testAll.isPending ||
                  test.isPending ||
                  data.filter((p) => p.type !== "mtproxy").length === 0
                }
                onClick={runTestAll}
              >
                {testAll.isPending ? t("proxies.testing_all") : t("proxies.test_all")}
              </Button>
              <Button
                variant="outline"
                iconLeft={<Layers size={16} strokeWidth={2} aria-hidden="true" />}
                onClick={() => setShowBulk(true)}
              >
                {t("proxies.bulk.add")}
              </Button>
              <Button
                iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
                onClick={() => setShowCreate(true)}
              >
                {t("proxies.add")}
              </Button>
            </>
          }
        />

        {data.length > 0 && (
          <div className="relative w-72">
            <Search
              size={15}
              strokeWidth={2}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("proxies.search_placeholder")}
              aria-label={t("proxies.search_placeholder")}
              className="h-8 pl-9"
            />
          </div>
        )}

        <DataTable
          columns={columns}
          data={filtered}
          empty={
            data.length > 0 ? (
              <EmptyState
                icon={Search}
                title={t("proxies.no_match_title")}
                action={
                  <Button
                    variant="outline"
                    onClick={() => {
                      setQuery("");
                      setHealthFilter("all");
                    }}
                  >
                    {t("proxies.clear_filters")}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Server}
                title={t("proxies.empty_title")}
                description={t("proxies.empty_hint")}
                action={
                  <Button
                    iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
                    onClick={() => setShowCreate(true)}
                  >
                    {t("proxies.add_first")}
                  </Button>
                }
              />
            )
          }
        />

        <HelpSection
          i18nKey="proxies.help"
          paragraphs={4}
          list={{
            titleKey: "proxies.help.list_title",
            itemsKey: "proxies.help.list_items",
            itemCount: 4,
          }}
        />

        {showCreate && (
          <CreateEditProxyDialog
            mode="create"
            onClose={() => setShowCreate(false)}
          />
        )}

        {showBulk && <BulkAddProxyDialog onClose={() => setShowBulk(false)} />}

        {editTarget && (
          <CreateEditProxyDialog
            mode="edit"
            proxy={editTarget}
            onClose={() => setEditTarget(null)}
          />
        )}

        {deleteTarget && (
          <DeleteProxyDialog
            proxy={deleteTarget}
            allProxies={data}
            onClose={() => setDeleteTarget(null)}
          />
        )}
      </section>
    </TooltipProvider>
  );
}

/* ---------------------------------------------------- DeleteProxyDialog */

function DeleteProxyDialog({
  proxy,
  allProxies,
  onClose,
}: {
  proxy: ProxySummary;
  allProxies: ProxySummary[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const del = useDeleteProxy();

  const hasAccounts = proxy.assigned_accounts_count > 0;

  // Candidates for reassignment: every other proxy.
  // Prefer alive proxies in same country → then alphabetical.
  const candidates = useMemo(() => {
    return [...allProxies]
      .filter((p) => p.id !== proxy.id)
      .sort((a, b) => {
        const aliveDiff = Number(b.is_alive ?? false) - Number(a.is_alive ?? false);
        if (aliveDiff !== 0) return aliveDiff;
        const sameA = a.country_code === proxy.country_code ? 1 : 0;
        const sameB = b.country_code === proxy.country_code ? 1 : 0;
        if (sameA !== sameB) return sameB - sameA;
        return a.label.localeCompare(b.label);
      });
  }, [allProxies, proxy]);

  const noCandidates = hasAccounts && candidates.length === 0;
  const [replacementId, setReplacementId] = useState<string>(
    candidates[0]?.id ?? "",
  );
  const [pending, setPending] = useState(false);

  const handleDelete = async () => {
    if (hasAccounts && !replacementId) return;
    setPending(true);
    try {
      await del.mutateAsync({
        id: proxy.id,
        reassign_to: hasAccounts ? replacementId : undefined,
      });
      if (hasAccounts) {
        const targetLabel =
          candidates.find((p) => p.id === replacementId)?.label ?? replacementId;
        pushToast({
          kind: "success",
          text: t("proxies.reassign_ok", {
            count: proxy.assigned_accounts_count,
            label: targetLabel,
          }),
        });
      } else {
        pushToast({ kind: "success", text: t("proxies.delete_ok", { label: proxy.label }) });
      }
      onClose();
    } catch (err) {
      pushToast({
        kind: "error",
        text: t("proxies.delete_failed"),
        detail: err instanceof ApiError ? err.detail : String(err),
      });
    } finally {
      setPending(false);
    }
  };

  const candidateOptions = candidates.map((p) => {
    const suffix =
      p.is_alive === false
        ? ` — ${t("proxies.health.dead")}`
        : p.is_alive === null
          ? ` — ${t("proxies.health.unknown")}`
          : "";
    const country = p.country_code ? ` (${p.country_code})` : "";
    return { value: p.id, label: `${p.label}${country}${suffix}` };
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t("proxies.delete_dialog.title", { label: proxy.label })}</DialogTitle>
          {hasAccounts ? (
            <DialogDescription>
              {t("proxies.delete_dialog.subtitle_reassign", {
                count: proxy.assigned_accounts_count,
              })}
            </DialogDescription>
          ) : (
            <DialogDescription>
              {t("proxies.delete_dialog.subtitle_safe")}
            </DialogDescription>
          )}
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Case: accounts attached, no other proxies exist → block delete */}
          {noCandidates && (
            <Alert tone="danger" title={t("proxies.delete_dialog.blocked_title")}>
              {t("proxies.delete_dialog.blocked_body", {
                count: proxy.assigned_accounts_count,
              })}
            </Alert>
          )}

          {/* Case: accounts attached, reassign target picker */}
          {hasAccounts && !noCandidates && (
            <div className="space-y-2">
              <Label htmlFor="reassign_target">
                {t("proxies.delete_dialog.reassign_label")}
              </Label>
              <p className="text-xs text-fg-muted">
                {t("proxies.delete_dialog.reassign_hint")}
              </p>
              {/* Show warning if selected target is dead */}
              {replacementId &&
                candidates.find((p) => p.id === replacementId)?.is_alive ===
                  false && (
                  <div className="flex items-center gap-1.5 text-xs text-warning">
                    <TriangleAlert size={13} strokeWidth={2} aria-hidden="true" />
                    {t("proxies.delete_dialog.target_dead_warn")}
                  </div>
                )}
              <SimpleSelect
                options={candidateOptions}
                value={replacementId}
                onValueChange={setReplacementId}
              />
            </div>
          )}

          {/* Case: no accounts — just confirm */}
          {!hasAccounts && (
            <p className="text-sm text-fg-secondary">
              {t("proxies.delete_dialog.no_accounts_body", { label: proxy.label })}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={pending} onClick={onClose}>
              {t("common.cancel")}
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            loading={pending}
            disabled={noCandidates || (hasAccounts && !replacementId)}
            onClick={handleDelete}
          >
            {hasAccounts
              ? t("proxies.delete_dialog.confirm_reassign")
              : t("proxies.delete_dialog.confirm_delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------- CreateEditProxyDialog */

interface CreateEditProxyDialogProps {
  mode: "create" | "edit";
  proxy?: ProxySummary;
  onClose: () => void;
}

function CreateEditProxyDialog({ mode, proxy, onClose }: CreateEditProxyDialogProps) {
  const { t } = useTranslation();
  const create = useCreateProxy();
  const update = useUpdateProxy();
  const probe = useProbeProxy();
  const testSaved = useTestProxy();
  const isEdit = mode === "edit";

  const [label, setLabel] = useState(proxy?.label ?? "");
  const [type, setType] = useState<ProxyType>(proxy?.type ?? "socks5");
  const [host, setHost] = useState(proxy?.host ?? "");
  const [port, setPort] = useState(proxy?.port?.toString() ?? "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState("");
  const [notes, setNotes] = useState(proxy?.notes ?? "");
  const [physicalType, setPhysicalType] = useState<string>(proxy?.physical_type ?? "unknown");
  const [error, setError] = useState<string | null>(null);
  const [probeResult, setProbeResult] = useState<ProxyProbeResult | null>(null);
  const [quickLine, setQuickLine] = useState("");
  const [quickState, setQuickState] = useState<"idle" | "ok" | "error">("idle");

  // Paste a whole proxy line → fill the fields below. Manual entry still works;
  // a line that doesn't parse leaves whatever is already typed alone.
  const handleQuickLine = (value: string) => {
    setQuickLine(value);
    if (!value.trim()) {
      setQuickState("idle");
      return;
    }
    const parsed = parseProxyLine(value);
    if (!parsed) {
      setQuickState("error");
      return;
    }
    if (parsed.type && TYPES.includes(parsed.type)) setType(parsed.type);
    setHost(parsed.host);
    setPort(String(parsed.port));
    setUsername(parsed.username ?? "");
    setPassword(parsed.password ?? "");
    setProbeResult(null);
    setQuickState("ok");
  };

  const portNum = parseInt(port, 10);
  const canProbe =
    host.trim().length > 0 &&
    !Number.isNaN(portNum) &&
    portNum > 0 &&
    portNum <= 65535 &&
    type !== "mtproxy";

  const handleProbe = async () => {
    setError(null);
    setProbeResult(null);
    try {
      if (isEdit && proxy) {
        // An existing proxy is checked with the login and password stored on
        // the server. The dialog never has them, so probing from the form
        // would test a proxy that needs auth without it and call it dead.
        const p = await testSaved.mutateAsync(proxy.id);
        setProbeResult({
          is_alive: p.is_alive === true,
          error: p.last_check_error,
          country_code: p.country_code,
          country_name: p.country_name,
          city: p.city,
          latency_ms: p.latency_ms,
        });
        return;
      }
      const r = await probe.mutateAsync({
        type,
        host: host.trim(),
        port: portNum,
        username: username || undefined,
        password: password || undefined,
        secret: type === "mtproxy" && secret ? secret : undefined,
      });
      setProbeResult(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("proxies.probe_error"));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEdit && proxy) {
        // PATCH updates only the mutable fields — connection params are
        // immutable (delete + recreate to change them). Calling create here
        // is what previously spawned a duplicate proxy.
        await update.mutateAsync({
          id: proxy.id,
          label,
          // Sent as typed: an emptied field must clear the stored note.
          notes,
          physical_type: physicalType,
        });
        pushToast({ kind: "success", text: t("proxies.update_ok", { label }) });
      } else {
        await create.mutateAsync({
          label,
          type,
          host: host.trim(),
          port: portNum,
          username: username || undefined,
          password: password || undefined,
          secret: type === "mtproxy" && secret ? secret : undefined,
          notes: notes || undefined,
          physical_type: physicalType,
        });
        pushToast({ kind: "success", text: t("proxies.create_ok", { label }) });
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError(t(isEdit ? "proxies.update_error" : "proxies.create_error"));
    }
  };

  const typeOptions = TYPES.map((tp) => ({ value: tp, label: tp.toUpperCase() }));
  const physicalTypeOptions = PROXY_PHYSICAL_TYPES.map((pt) => ({
    value: pt,
    label: t(`proxies.physical_type.${pt}`),
  }));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? t("proxies.create_title") : t("proxies.edit_title")}
          </DialogTitle>
          {mode === "create" && (
            <DialogDescription>{t("proxies.create_subtitle")}</DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} id="proxy-form">
          <DialogBody className="space-y-4">
            {!isEdit && (
              <div className="space-y-2">
                <Label htmlFor="proxy_quick">{t("proxies.quick.label")}</Label>
                <Input
                  id="proxy_quick"
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-xs"
                  placeholder="socks5://user:pass@82.41.246.178:42020"
                  value={quickLine}
                  onChange={(e) => handleQuickLine(e.target.value)}
                />
                <p
                  className={`text-xs ${quickState === "error" ? "text-danger" : "text-fg-muted"}`}
                >
                  {quickState === "ok"
                    ? t("proxies.quick.filled")
                    : quickState === "error"
                      ? t("proxies.quick.error")
                      : t("proxies.quick.hint")}
                </p>
              </div>
            )}

            {/* Label */}
            <div className="space-y-2">
              <Label htmlFor="proxy_label">{t("proxies.field.label")}</Label>
              <Input
                id="proxy_label"
                required
                placeholder="ru-residential-1"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label htmlFor="proxy_type">{t("proxies.field.type")}</Label>
              <SimpleSelect
                options={typeOptions}
                value={type}
                onValueChange={(v) => setType(v as ProxyType)}
                disabled={isEdit}
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  {t("proxies.edit_connection_locked")}
                </p>
              )}
            </div>

            {/* Physical type — drives the session↔proxy coherence advisory */}
            <div className="space-y-2">
              <Label htmlFor="proxy_physical_type">{t("proxies.field.physical_type")}</Label>
              <SimpleSelect
                options={physicalTypeOptions}
                value={physicalType}
                onValueChange={setPhysicalType}
              />
            </div>

            {/* Host + Port */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="proxy_host">{t("proxies.field.host")}</Label>
                <Input
                  id="proxy_host"
                  required
                  disabled={isEdit}
                  placeholder="proxy.example.com"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="proxy_port">{t("proxies.field.port")}</Label>
                <Input
                  id="proxy_port"
                  required
                  disabled={isEdit}
                  inputMode="numeric"
                  pattern="[0-9]+"
                  placeholder="1080"
                  value={port}
                  onChange={(e) => setPort(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            </div>

            {/* Auth fields — not for mtproxy */}
            {type !== "mtproxy" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="proxy_username">{t("proxies.field.username")}</Label>
                  <Input
                    id="proxy_username"
                    autoComplete="off"
                    disabled={isEdit}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="proxy_password">{t("proxies.field.password")}</Label>
                  <Input
                    id="proxy_password"
                    type="password"
                    autoComplete="off"
                    disabled={isEdit}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Secret — mtproxy only */}
            {type === "mtproxy" && (
              <div className="space-y-2">
                <Label htmlFor="proxy_secret">{t("proxies.field.secret")}</Label>
                <Input
                  id="proxy_secret"
                  required
                  disabled={isEdit}
                  placeholder="ee…"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="proxy_notes">{t("proxies.field.notes")}</Label>
              <Input
                id="proxy_notes"
                placeholder={t("proxies.field.notes_placeholder")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Probe result */}
            {probeResult && (
              <Alert
                tone={probeResult.is_alive ? "success" : "danger"}
                title={
                  probeResult.is_alive
                    ? t("proxies.probe.alive")
                    : t("proxies.probe.dead")
                }
              >
                <div className="space-y-0.5">
                  {probeResult.is_alive && probeResult.latency_ms != null && (
                    <p
                      className={`font-mono text-xs ${
                        probeResult.latency_ms >= SLOW_PROXY_MS ? "text-warning" : ""
                      }`}
                    >
                      {probeResult.latency_ms} ms
                      {probeResult.latency_ms >= SLOW_PROXY_MS &&
                        ` ⚠ ${t("proxies.slow_ping_hint")}`}
                    </p>
                  )}
                  {probeResult.country_code && (
                    <p className="text-xs">
                      {t("proxies.probe.country")}: {probeResult.country_code}
                      {probeResult.city && (
                        <span className="text-fg-muted"> ({probeResult.city})</span>
                      )}
                    </p>
                  )}
                  {probeResult.error && (
                    <p className="font-mono text-xs text-danger">{probeResult.error}</p>
                  )}
                </div>
              </Alert>
            )}

            {/* Inline error */}
            {error && (
              <Alert tone="danger">{error}</Alert>
            )}
          </DialogBody>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
          </DialogClose>
          <Tooltip
            content={
              type === "mtproxy"
                ? t("proxies.mtproxy_no_test")
                : !canProbe
                  ? t("proxies.probe.fill_first")
                  : undefined
            }
          >
            <Button
              type="button"
              variant="secondary"
              iconLeft={<RefreshCw size={14} strokeWidth={2} aria-hidden="true" />}
              loading={probe.isPending || testSaved.isPending}
              disabled={!canProbe || probe.isPending || testSaved.isPending}
              onClick={handleProbe}
            >
              {t("proxies.probe.run")}
            </Button>
          </Tooltip>
          <Button
            type="submit"
            form="proxy-form"
            loading={isEdit ? update.isPending : create.isPending}
          >
            {mode === "create" ? t("proxies.create") : t("proxies.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------- BulkAddProxyDialog */

function BulkAddProxyDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const bulk = useBulkCreateProxies();
  const [type, setType] = useState<ProxyType>("socks5");
  const [physicalType, setPhysicalType] = useState<string>("mobile");
  const [labelPrefix, setLabelPrefix] = useState("");
  const [rotationUrl, setRotationUrl] = useState("");
  const [text, setText] = useState("");
  const [result, setResult] = useState<ProxyBulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const typeOptions = TYPES.map((tp) => ({ value: tp, label: tp.toUpperCase() }));
  const physicalTypeOptions = PROXY_PHYSICAL_TYPES.map((pt) => ({
    value: pt,
    label: t(`proxies.physical_type.${pt}`),
  }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      const r = await bulk.mutateAsync({
        type,
        physical_type: physicalType,
        label_prefix: labelPrefix.trim() || undefined,
        rotation_url: rotationUrl.trim() || undefined,
        text,
      });
      setResult(r);
      if (r.created.length > 0) {
        pushToast({ kind: "success", text: t("proxies.bulk.created_ok", { count: r.created.length }) });
      }
      if (r.errors.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("proxies.bulk.error"));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t("proxies.bulk.title")}</DialogTitle>
          <DialogDescription>{t("proxies.bulk.subtitle")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} id="proxy-bulk-form">
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="bulk_type">{t("proxies.field.type")}</Label>
                <SimpleSelect
                  options={typeOptions}
                  value={type}
                  onValueChange={(v) => setType(v as ProxyType)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bulk_physical_type">{t("proxies.field.physical_type")}</Label>
                <SimpleSelect
                  options={physicalTypeOptions}
                  value={physicalType}
                  onValueChange={setPhysicalType}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk_prefix">{t("proxies.bulk.prefix_label")}</Label>
              <Input
                id="bulk_prefix"
                placeholder={t("proxies.bulk.prefix_placeholder")}
                value={labelPrefix}
                onChange={(e) => setLabelPrefix(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk_rotation">{t("proxies.bulk.rotation_label")}</Label>
              <Input
                id="bulk_rotation"
                placeholder="https://gw.dataimpulse.com:777/api/rotate_ip?port={port}"
                value={rotationUrl}
                onChange={(e) => setRotationUrl(e.target.value)}
              />
              <p className="text-xs text-fg-muted">{t("proxies.bulk.rotation_hint")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk_text">{t("proxies.bulk.lines_label")}</Label>
              <textarea
                id="bulk_text"
                required
                rows={8}
                spellCheck={false}
                className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 font-mono text-xs text-fg-primary outline-none focus:border-accent"
                placeholder={"login:password@gw.dataimpulse.com:10000\nlogin:password@gw.dataimpulse.com:10001\nhost:port:login:password\n1.2.3.4:1080"}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <p className="text-xs text-fg-muted">{t("proxies.bulk.lines_hint")}</p>
            </div>

            {result && result.errors.length > 0 && (
              <Alert tone="warning" title={t("proxies.bulk.partial", { created: result.created.length, failed: result.errors.length })}>
                <ul className="mt-1 space-y-0.5 font-mono text-xs">
                  {result.errors.slice(0, 12).map((er) => (
                    <li key={er.line_number}>
                      <span className="text-fg-muted">#{er.line_number}</span> {er.reason}
                      <span className="text-fg-muted"> — {er.raw}</span>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {error && <Alert tone="danger">{error}</Alert>}
          </DialogBody>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={onClose}>
              {result ? t("common.close") : t("common.cancel")}
            </Button>
          </DialogClose>
          <Button type="submit" form="proxy-bulk-form" loading={bulk.isPending}>
            {t("proxies.bulk.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
